import { useCallback, useRef, useState } from 'react';

import type { NoRowsOverlayContext } from '@/features/table/components/NoRowsOverlay';
import type { GridApi } from 'ag-grid-community';
import type { AgGridReactProps } from 'ag-grid-react';

type GridReadyHandler<T> = NonNullable<AgGridReactProps<T>['onGridReady']>;
type GridReadyParams<T> = Parameters<GridReadyHandler<T>>[0];

interface UseNoRowsOverlayOptions<T> {
  onGridReady?: GridReadyHandler<T>;
  totalRowCount?: number;
}

/**
 * Hook that manages NoRowsOverlay visibility for AG Grid.
 * Sets _isFiltered context flag when empty due to filtering vs no data.
 * Also tracks displayed row count for footer display.
 */
export function useNoRowsOverlay<T>(options: UseNoRowsOverlayOptions<T> = {}) {
  const { onGridReady, totalRowCount = 0 } = options;
  const gridApiRef = useRef<GridApi<T> | null>(null);
  const totalRowCountRef = useRef(totalRowCount);
  totalRowCountRef.current = totalRowCount;

  // Track displayed row count for footer (updates when filtering)
  const [displayedRowCount, setDisplayedRowCount] = useState(totalRowCount);

  const updateOverlayVisibility = useCallback(() => {
    const api = gridApiRef.current;
    if (!api) return;

    const currentDisplayedCount = api.getDisplayedRowCount();
    setDisplayedRowCount(currentDisplayedCount);

    if (currentDisplayedCount === 0) {
      const isFiltered = totalRowCountRef.current > 0;

      const currentContext = api.getGridOption('context') as NoRowsOverlayContext | undefined;
      if (currentContext) {
        api.updateGridOptions({
          context: {
            ...currentContext,
            _isFiltered: isFiltered
          }
        });
      }

      api.showNoRowsOverlay();
    } else {
      api.hideOverlay();
    }
  }, []);

  const handleGridReady = useCallback(
    (params: GridReadyParams<T>) => {
      gridApiRef.current = params.api;
      onGridReady?.(params);
      updateOverlayVisibility();
    },
    [onGridReady, updateOverlayVisibility]
  );

  const handleModelUpdated = useCallback(() => {
    updateOverlayVisibility();
  }, [updateOverlayVisibility]);

  return {
    gridApiRef,
    handleGridReady,
    handleModelUpdated,
    displayedRowCount
  };
}
